/**
 * Healthwave cart guard — pharmacy purchase-limit enforcement.
 *
 * Loaded synchronously in theme.liquid <head> BEFORE {{ content_for_header }}
 * so it wraps fetch/XHR before any app (cart drawers, upsells) captures them.
 * This is the primary enforcement layer: checkout validation functions are
 * Plus-only, so the storefront must block and the pharmacist review backstops.
 *
 * Three mechanisms:
 *  1. Intercept /cart/add, /cart/change, /cart/update (fetch + XHR + forms):
 *     evaluate against rules from the app proxy and block violations.
 *  2. Reconcile: on load and after every cart mutation, audit the cart and
 *     clamp any over-limit line that slipped through another code path.
 *  3. Pharmacist-questions gate: capture-phase click/submit blocker that other
 *     scripts can't outrace (snippets register via __hwGuard.registerQuestionGate).
 */
(function () {
  "use strict";

  if (window.__hwGuard) return;

  var PROXY_BASE = "/apps/rxcopilot";
  var originalFetch = window.fetch.bind(window);

  var guard = (window.__hwGuard = {
    originalFetch: originalFetch,
    registerQuestionGate: function (fn) {
      questionGates.push(fn);
    },
  });

  var questionGates = [];
  var rulesCache = new Map();
  var inflight = new Map();
  var reconciling = false;

  function isNumericId(id) {
    return /^\d+$/.test(String(id));
  }

  function fetchRulesFor(productIds) {
    var missing = productIds.filter(function (id) {
      return id && isNumericId(id) && !rulesCache.has(String(id)) && !inflight.has(String(id));
    });
    if (missing.length === 0) return Promise.resolve();

    var url = PROXY_BASE + "/cart-rules?ids=" + encodeURIComponent(missing.join(","));
    var promise = originalFetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        if (!r.ok) throw new Error("rules fetch failed: " + r.status);
        return r.json();
      })
      .then(function (data) {
        var products = data.products || {};
        var groups = data.groups || {};
        missing.forEach(function (id) {
          var key = String(id);
          var p = products[key] || { maxQty: null, maxQtyMessage: null, groupIds: [] };
          var groupRefs = (p.groupIds || [])
            .map(function (gid) { return groups[gid]; })
            .filter(Boolean);
          rulesCache.set(key, {
            maxQty: p.maxQty,
            maxQtyMessage: p.maxQtyMessage || null,
            groups: groupRefs,
          });
        });
      })
      .catch(function (err) {
        console.warn("[HWCartGuard] rules fetch failed", err);
        missing.forEach(function (id) {
          if (!rulesCache.has(String(id))) {
            rulesCache.set(String(id), { maxQty: null, maxQtyMessage: null, groups: [] });
          }
        });
      });

    missing.forEach(function (id) { inflight.set(String(id), promise); });
    return promise.finally(function () {
      missing.forEach(function (id) { inflight.delete(String(id)); });
    });
  }

  function getCart() {
    return originalFetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    }).then(function (r) { return r.json(); });
  }

  function resolveVariantToProduct(variantId, cart) {
    // 1. The variant may already be in the cart.
    if (cart && cart.items) {
      for (var i = 0; i < cart.items.length; i++) {
        if (String(cart.items[i].variant_id) === String(variantId)) {
          return Promise.resolve(String(cart.items[i].product_id));
        }
      }
    }
    // 2. The current page's product (covers all product-page adds, incl. any variant).
    return currentPageProduct().then(function (pp) {
      if (pp && pp.variantIds.indexOf(String(variantId)) !== -1) {
        return String(pp.id);
      }
      // 3. /variants/{id}.js — on this store it returns variant JSON WITHOUT
      //    product_id (its `id` is the VARIANT id — never use it as a product
      //    id). Only trust an explicit product_id field.
      return originalFetch("/variants/" + variantId + ".js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (v) {
          if (v && v.product_id != null && isNumericId(v.product_id)) {
            return String(v.product_id);
          }
          // Unresolved: fail open pre-add — the post-mutation reconciler
          // clamps anything that lands over-limit.
          return null;
        })
        .catch(function () { return null; });
    });
  }

  function evaluateEffective(changedProductId, effectiveItems) {
    var rule = rulesCache.get(String(changedProductId));
    if (!rule) return null;

    var productTotal = 0;
    effectiveItems.forEach(function (item) {
      if (String(item.product_id) === String(changedProductId)) productTotal += item.quantity;
    });

    if (rule.maxQty !== null && productTotal > rule.maxQty) {
      return { kind: "product", max: rule.maxQty, current: productTotal, customerMessage: rule.maxQtyMessage || null };
    }

    for (var i = 0; i < rule.groups.length; i++) {
      var g = rule.groups[i];
      var groupTotal = 0;
      effectiveItems.forEach(function (item) {
        if (g.productIds.indexOf(String(item.product_id)) !== -1) groupTotal += item.quantity;
      });
      if (groupTotal > g.maxQuantity) {
        return { kind: "group", groupName: g.name, max: g.maxQuantity, current: groupTotal, customerMessage: g.customerMessage || null };
      }
    }
    return null;
  }

  function checkRequestedItems(items) {
    return getCart().then(function (cart) {
      var resolutions = items.map(function (it) {
        return resolveVariantToProduct(it.variantId, cart).then(function (pid) {
          return pid ? { productId: pid, quantity: it.quantity } : null;
        });
      });
      return Promise.all(resolutions).then(function (adds) {
        adds = adds.filter(Boolean);
        if (adds.length === 0) return null;
        return fetchRulesFor(adds.map(function (a) { return a.productId; })).then(function () {
          var effective = (cart.items || []).map(function (item) {
            return { product_id: item.product_id, quantity: item.quantity };
          });
          adds.forEach(function (a) {
            effective.push({ product_id: a.productId, quantity: a.quantity });
          });
          for (var i = 0; i < adds.length; i++) {
            var violation = evaluateEffective(adds[i].productId, effective);
            if (violation) return violation;
          }
          return null;
        });
      });
    });
  }

  function findCartLine(cart, ref) {
    var items = cart.items || [];
    if (typeof ref === "number" || /^\d{1,3}$/.test(String(ref))) {
      var idx = parseInt(ref, 10);
      if (idx >= 1 && idx <= items.length) return items[idx - 1];
    }
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === ref) return items[i];
      if (String(items[i].variant_id) === String(ref)) return items[i];
    }
    return null;
  }

  function checkCartMutations(mutations) {
    return getCart().then(function (cart) {
      var increases = [];
      mutations.forEach(function (m) {
        var line = findCartLine(cart, m.ref);
        if (line && m.quantity > line.quantity) increases.push({ line: line, quantity: m.quantity });
      });
      if (increases.length === 0) return null;

      return fetchRulesFor(increases.map(function (inc) { return String(inc.line.product_id); })).then(function () {
        var effective = (cart.items || []).map(function (item) {
          var target = null;
          increases.forEach(function (inc) {
            if (inc.line.key === item.key) target = inc.quantity;
          });
          return { product_id: item.product_id, quantity: target !== null ? target : item.quantity };
        });
        for (var i = 0; i < increases.length; i++) {
          var violation = evaluateEffective(String(increases[i].line.product_id), effective);
          if (violation) return violation;
        }
        return null;
      });
    });
  }

  // ---- reconciler: audit the live cart and clamp anything over-limit ----

  function reconcileCart() {
    if (reconciling) return Promise.resolve();
    reconciling = true;

    return getCart()
      .then(function (cart) {
        var items = cart.items || [];
        if (items.length === 0) return null;
        var ids = items.map(function (i) { return String(i.product_id); });
        return fetchRulesFor(ids).then(function () {
          // Determine per-line allowed quantities.
          var changes = [];
          var violationShown = null;

          // Per-product caps.
          var productTotals = {};
          items.forEach(function (item) {
            var pid = String(item.product_id);
            productTotals[pid] = (productTotals[pid] || 0) + item.quantity;
          });

          // Build clamped quantities, walking lines in order.
          var groupBudget = {};
          var productBudget = {};
          items.forEach(function (item) {
            var pid = String(item.product_id);
            var rule = rulesCache.get(pid);
            var allowed = item.quantity;

            if (rule) {
              if (rule.maxQty !== null) {
                if (productBudget[pid] === undefined) productBudget[pid] = rule.maxQty;
                allowed = Math.min(allowed, Math.max(0, productBudget[pid]));
              }
              (rule.groups || []).forEach(function (g) {
                if (groupBudget[g.id] === undefined) groupBudget[g.id] = g.maxQuantity;
                allowed = Math.min(allowed, Math.max(0, groupBudget[g.id]));
              });

              if (allowed < item.quantity) {
                changes.push({ key: item.key, quantity: allowed });
                if (!violationShown) {
                  var grp = (rule.groups || [])[0];
                  violationShown = {
                    kind: grp ? "group" : "product",
                    groupName: grp ? grp.name : undefined,
                    max: grp ? grp.maxQuantity : rule.maxQty,
                    customerMessage: (grp && grp.customerMessage) || rule.maxQtyMessage || null,
                    clamped: true,
                  };
                }
              }

              // Spend budgets.
              if (rule.maxQty !== null) productBudget[pid] -= allowed;
              (rule.groups || []).forEach(function (g) { groupBudget[g.id] -= allowed; });
            }
          });

          if (changes.length === 0) return null;

          // Apply clamps sequentially with the ORIGINAL fetch (avoid self-interception).
          var chain = Promise.resolve();
          changes.forEach(function (c) {
            chain = chain.then(function () {
              return originalFetch("/cart/change.js", {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ id: c.key, quantity: c.quantity }),
              });
            });
          });
          return chain.then(function () { return violationShown; });
        });
      })
      .then(function (violation) {
        if (violation) {
          showBlockModal(violation, { reloadOnClose: true });
        }
      })
      .catch(function (err) {
        console.warn("[HWCartGuard] reconcile failed", err);
      })
      .finally(function () {
        reconciling = false;
      });
  }

  // ---- modal ----

  function ensureStyles() {
    if (document.getElementById("hw-cart-guard-style")) return;
    var style = document.createElement("style");
    style.id = "hw-cart-guard-style";
    style.textContent =
      ".hw-cg-modal{position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center}" +
      ".hw-cg-backdrop{position:absolute;inset:0;background:rgba(14,27,77,.45)}" +
      ".hw-cg-dialog{position:relative;background:#fff;border-radius:18px;max-width:420px;width:calc(100% - 40px);padding:28px;box-shadow:0 10px 40px rgba(0,0,0,.2);font-family:inherit}" +
      ".hw-cg-heading{margin:0 0 10px;font-size:1.25rem;color:#0e1b4d}" +
      ".hw-cg-body{margin:0 0 20px;font-size:1rem;color:#0e1b4d;opacity:.85}" +
      ".hw-cg-ok{background:#00bfb3;color:#fff;border:none;border-radius:40px;padding:10px 28px;font-size:1rem;cursor:pointer}" +
      ".hw-cg-close{position:absolute;top:10px;right:14px;background:none;border:none;font-size:22px;cursor:pointer;color:#0e1b4d}";
    document.head.appendChild(style);
  }

  function showBlockModal(violation, opts) {
    ensureStyles();
    var existing = document.getElementById("hw-cart-guard-modal");
    if (existing) existing.remove();

    var heading = violation.kind === "group" ? "Limit reached for " + violation.groupName : "Limit reached";
    var body;
    if (violation.customerMessage) {
      body = violation.customerMessage;
    } else if (violation.kind === "group") {
      body = "Only " + violation.max + " " + violation.groupName + " " + (violation.max === 1 ? "product" : "products") + " can be in your cart at once.";
    } else {
      body = "Only " + violation.max + " of this product can be in your cart at once.";
    }
    if (violation.clamped) {
      body += " We've adjusted your basket to the allowed amount.";
    }

    var modal = document.createElement("div");
    modal.id = "hw-cart-guard-modal";
    modal.className = "hw-cg-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.innerHTML =
      '<div class="hw-cg-backdrop" data-hw-close></div>' +
      '<div class="hw-cg-dialog">' +
      '<button type="button" class="hw-cg-close" aria-label="Close" data-hw-close>&times;</button>' +
      '<h2 class="hw-cg-heading"></h2><p class="hw-cg-body"></p>' +
      '<button type="button" class="hw-cg-ok" data-hw-close>OK</button></div>';
    modal.querySelector(".hw-cg-heading").textContent = heading;
    modal.querySelector(".hw-cg-body").textContent = body;

    function close() {
      modal.remove();
      document.removeEventListener("keydown", onKey);
      if (opts && opts.reloadOnClose) window.location.reload();
    }
    function onKey(e) { if (e.key === "Escape") close(); }
    modal.addEventListener("click", function (e) {
      if (e.target.hasAttribute("data-hw-close")) close();
    });
    document.addEventListener("keydown", onKey);
    (document.body || document.documentElement).appendChild(modal);
    var ok = modal.querySelector(".hw-cg-ok");
    if (ok) ok.focus();
    document.dispatchEvent(new CustomEvent("hw:cart-restriction-blocked", { detail: violation }));
  }

  function blockedResponse(violation) {
    return new Response(
      JSON.stringify({
        status: 422,
        message: "Cart restriction",
        description: violation.kind === "group" ? "Limit reached for " + violation.groupName : "Product limit reached",
      }),
      { status: 422, headers: { "Content-Type": "application/json" } },
    );
  }

  // ---- payload parsing ----

  function parseAddPayload(body) {
    if (!body) return [];
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      var ids = body.getAll("id").map(String);
      if (ids.length === 0) return [];
      var qty = parseInt(body.get("quantity"), 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      return ids.map(function (id) { return { variantId: id, quantity: qty }; });
    }
    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) body = body.toString();
    if (typeof body === "string") {
      try {
        var parsed = JSON.parse(body);
        if (parsed && Array.isArray(parsed.items)) {
          return parsed.items.map(function (it) {
            return { variantId: String(it.id), quantity: parseInt(it.quantity, 10) || 1 };
          });
        }
        if (parsed && parsed.id) {
          return [{ variantId: String(parsed.id), quantity: parseInt(parsed.quantity, 10) || 1 }];
        }
      } catch (e) {
        var params = new URLSearchParams(body);
        var pid = params.get("id");
        if (!pid) return [];
        return [{ variantId: pid, quantity: parseInt(params.get("quantity"), 10) || 1 }];
      }
    }
    return [];
  }

  function parseChangePayload(body, url) {
    var ref = null, qty = null;
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      ref = body.get("line") || body.get("id");
      qty = body.get("quantity");
    } else if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      ref = body.get("line") || body.get("id");
      qty = body.get("quantity");
    } else if (typeof body === "string") {
      try {
        var parsed = JSON.parse(body);
        ref = parsed.line != null ? parsed.line : parsed.id;
        qty = parsed.quantity;
      } catch (e) {
        var params = new URLSearchParams(body);
        ref = params.get("line") || params.get("id");
        qty = params.get("quantity");
      }
    }
    if ((qty === null || qty === undefined || qty === "") && url) {
      try {
        var u = new URL(url, window.location.origin);
        if (u.searchParams.get("quantity") !== null) qty = u.searchParams.get("quantity");
        if (!ref) ref = u.searchParams.get("line") || u.searchParams.get("id");
      } catch (e) { /* noop */ }
    }
    var quantity = parseInt(qty, 10);
    if (!ref || isNaN(quantity)) return [];
    return [{ ref: ref, quantity: quantity }];
  }

  function parseUpdatePayload(body) {
    var mutations = [];
    function fromObj(updates) {
      Object.keys(updates).forEach(function (key) {
        var q = parseInt(updates[key], 10);
        if (!isNaN(q)) mutations.push({ ref: key, quantity: q });
      });
    }
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      var positional = body.getAll("updates[]");
      if (positional.length > 0) {
        positional.forEach(function (q, i) {
          var qty = parseInt(q, 10);
          if (!isNaN(qty)) mutations.push({ ref: i + 1, quantity: qty });
        });
      } else {
        body.forEach(function (value, key) {
          var m = key.match(/^updates\[(.+)\]$/);
          if (m) {
            var q = parseInt(value, 10);
            if (!isNaN(q)) mutations.push({ ref: m[1], quantity: q });
          }
        });
      }
    } else if (typeof body === "string") {
      try {
        var parsed = JSON.parse(body);
        if (parsed && parsed.updates) {
          if (Array.isArray(parsed.updates)) {
            parsed.updates.forEach(function (q, i) {
              var qty = parseInt(q, 10);
              if (!isNaN(qty)) mutations.push({ ref: i + 1, quantity: qty });
            });
          } else fromObj(parsed.updates);
        }
      } catch (e) {
        var params = new URLSearchParams(body);
        var idx = 0;
        params.forEach(function (value, key) {
          if (key === "updates[]") {
            idx += 1;
            var q = parseInt(value, 10);
            if (!isNaN(q)) mutations.push({ ref: idx, quantity: q });
          } else {
            var m = key.match(/^updates\[(.+)\]$/);
            if (m) {
              var q2 = parseInt(value, 10);
              if (!isNaN(q2)) mutations.push({ ref: m[1], quantity: q2 });
            }
          }
        });
      }
    }
    return mutations;
  }

  var ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var CHANGE_RE = /\/cart\/change(\.js)?(\?|$)/;
  var UPDATE_RE = /\/cart\/update(\.js)?(\?|$)/;

  function isCartPage() { return /^\/cart\/?$/.test(window.location.pathname); }

  // ---- fetch interception (installed before any app captures fetch) ----

  window.fetch = function (input, init) {
    var args = arguments;
    try {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var body = init && init.body;

      if (url && ADD_RE.test(url)) {
        var items = parseAddPayload(body);
        if (items.length > 0) {
          return checkRequestedItems(items).then(function (violation) {
            if (violation) {
              showBlockModal(violation);
              return blockedResponse(violation);
            }
            return originalFetch.apply(null, args).then(function (resp) {
              setTimeout(reconcileCart, 800);
              return resp;
            });
          });
        }
      }

      if (url && (CHANGE_RE.test(url) || UPDATE_RE.test(url))) {
        var mutations = CHANGE_RE.test(url) ? parseChangePayload(body, url) : parseUpdatePayload(body);
        if (mutations.length > 0) {
          return checkCartMutations(mutations).then(function (violation) {
            if (violation) {
              showBlockModal(violation, { reloadOnClose: isCartPage() });
              return blockedResponse(violation);
            }
            return originalFetch.apply(null, args).then(function (resp) {
              setTimeout(reconcileCart, 800);
              return resp;
            });
          });
        }
      }
    } catch (e) {
      console.warn("[HWCartGuard] fetch hook error", e);
    }
    return originalFetch.apply(null, args);
  };

  // ---- XHR interception ----

  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__hwUrl = url;
    this.__hwMethod = method;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    var xhr = this;
    var isPost = String(xhr.__hwMethod || "").toUpperCase() === "POST";
    if (xhr.__hwUrl && isPost) {
      var checker = null;
      var opts = undefined;
      if (ADD_RE.test(xhr.__hwUrl)) {
        var items = parseAddPayload(body);
        if (items.length > 0) checker = checkRequestedItems(items);
      } else if (CHANGE_RE.test(xhr.__hwUrl) || UPDATE_RE.test(xhr.__hwUrl)) {
        var mutations = CHANGE_RE.test(xhr.__hwUrl) ? parseChangePayload(body, xhr.__hwUrl) : parseUpdatePayload(body);
        if (mutations.length > 0) {
          checker = checkCartMutations(mutations);
          opts = { reloadOnClose: isCartPage() };
        }
      }
      if (checker) {
        checker.then(function (violation) {
          if (violation) {
            showBlockModal(violation, opts);
            try { xhr.abort(); } catch (e) { /* noop */ }
            return;
          }
          origSend.call(xhr, body);
          setTimeout(reconcileCart, 800);
        });
        return;
      }
    }
    return origSend.apply(this, arguments);
  };

  // ---- capture-phase gates (registered before app scripts can register theirs) ----

  document.addEventListener(
    "click",
    function (e) {
      if (questionGates.length === 0) return;
      var btn = e.target && e.target.closest && e.target.closest('button[type="submit"], [id^="ProductSubmitButton"], .shopify-payment-button button');
      if (!btn) return;
      for (var i = 0; i < questionGates.length; i++) {
        try {
          var verdict = questionGates[i](btn);
          if (verdict && verdict.blocked) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
          }
        } catch (err) { /* noop */ }
      }
    },
    true,
  );

  document.addEventListener(
    "submit",
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== "FORM" || !form.action) return;

      // Pharmacist questions gate first.
      if (/\/cart\/add\b/.test(form.action) && questionGates.length > 0) {
        for (var i = 0; i < questionGates.length; i++) {
          try {
            var verdict = questionGates[i](form);
            if (verdict && verdict.blocked) {
              e.preventDefault();
              e.stopImmediatePropagation();
              return;
            }
          } catch (err) { /* noop */ }
        }
      }

      if (/\/cart\/add\b/.test(form.action)) {
        // Themes with JS product forms (e.g. <product-form>) convert this submit
        // into a fetch we already gate — intercepting here too causes a double
        // preventDefault race where nothing wins. Only gate truly native forms.
        if (form.closest("product-form")) return;
        var fd = new FormData(form);
        var items = parseAddPayload(fd);
        if (items.length === 0) return;
        e.preventDefault();
        checkRequestedItems(items).then(function (violation) {
          if (violation) {
            showBlockModal(violation);
            return;
          }
          form.submit();
        });
        return;
      }

      if (/\/cart\/?(\?|$)/.test(form.action)) {
        var cfd = new FormData(form);
        var mutations = parseUpdatePayload(cfd);
        if (mutations.length === 0) return;
        e.preventDefault();
        checkCartMutations(mutations).then(function (violation) {
          if (violation) {
            showBlockModal(violation, { reloadOnClose: true });
            return;
          }
          form.submit();
        });
      }
    },
    true,
  );

  // ---- rule-aware steppers: cap quantity inputs / disable "+" at the limit ----

  var pageProductPromise = null;
  function currentPageProduct() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!m) return Promise.resolve(null);
    if (!pageProductPromise) {
      pageProductPromise = originalFetch("/products/" + m[1] + ".js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.id) return null;
          return {
            id: String(p.id),
            variantIds: (p.variants || []).map(function (v) { return String(v.id); }),
          };
        })
        .catch(function () { return null; });
    }
    return pageProductPromise;
  }

  function currentProductId() {
    return currentPageProduct().then(function (pp) { return pp ? pp.id : null; });
  }

  // Remaining headroom for a product given the current cart (can be negative).
  function remainingFor(pid, cart) {
    var rule = rulesCache.get(String(pid));
    if (!rule) return Infinity;
    var rem = Infinity;
    var items = cart.items || [];
    if (rule.maxQty !== null) {
      var pTotal = 0;
      items.forEach(function (i) { if (String(i.product_id) === String(pid)) pTotal += i.quantity; });
      rem = Math.min(rem, rule.maxQty - pTotal);
    }
    (rule.groups || []).forEach(function (g) {
      var gTotal = 0;
      items.forEach(function (i) { if (g.productIds.indexOf(String(i.product_id)) !== -1) gTotal += i.quantity; });
      rem = Math.min(rem, g.maxQuantity - gTotal);
    });
    return rem;
  }

  function setStepperMax(input, max) {
    input.setAttribute("max", String(max));
    input.dataset.hwMax = String(max);
    var val = parseInt(input.value, 10) || 1;
    var wrapper = input.closest("quantity-input") || input.parentElement;
    var plus = wrapper ? wrapper.querySelector('button[name="plus"]') : null;
    if (plus) {
      var atMax = val >= max;
      plus.disabled = atMax;
      plus.toggleAttribute("disabled", atMax);
      plus.classList.toggle("hw-cg-plus-disabled", atMax);
    }
    if (val > max && max >= 1) input.value = String(max);
  }

  function limitSteppers() {
    getCart().then(function (cart) {
      var ids = (cart.items || []).map(function (i) { return String(i.product_id); });
      currentProductId().then(function (pagePid) {
        if (pagePid) ids.push(pagePid);
        if (ids.length === 0) return;
        fetchRulesFor(ids).then(function () {
          // Product page steppers (quantity for a prospective add).
          if (pagePid && rulesCache.get(pagePid)) {
            var rem = remainingFor(pagePid, cart);
            if (rem !== Infinity) {
              document.querySelectorAll('input[name="quantity"]').forEach(function (input) {
                if (input.closest(".cart-items, cart-items, #CartDrawer")) return;
                setStepperMax(input, Math.max(1, rem));
                // Nothing left to add: cap at 1 and disable plus outright.
                if (rem <= 0) {
                  var wrapper = input.closest("quantity-input") || input.parentElement;
                  var plus = wrapper ? wrapper.querySelector('button[name="plus"]') : null;
                  if (plus) { plus.disabled = true; plus.toggleAttribute("disabled", true); }
                }
              });
            }
          }
          // Cart page / drawer line steppers.
          document.querySelectorAll('input[name="updates[]"]').forEach(function (input) {
            var idx = parseInt(input.dataset.index || input.getAttribute("data-index"), 10);
            var line = !isNaN(idx) ? (cart.items || [])[idx - 1] : null;
            if (!line) return;
            var rule = rulesCache.get(String(line.product_id));
            if (!rule) return;
            var rem = remainingFor(String(line.product_id), cart);
            var allowedMax = line.quantity + Math.max(0, rem === Infinity ? 99 : rem);
            setStepperMax(input, Math.max(1, allowedMax));
          });
        });
      });
    }).catch(function () { /* noop */ });
  }

  // ---- boot: reconcile + steppers on load, after mutations, and periodically ----

  function boot() {
    setTimeout(reconcileCart, 500);
    setTimeout(limitSteppers, 800);
    setInterval(function () {
      if (document.hidden) return;
      limitSteppers();
    }, 4000);
  }

  var origReconcile = reconcileCart;
  reconcileCart = function () {
    return origReconcile().then(function (r) {
      limitSteppers();
      return r;
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
